import moment from "moment-timezone";
import range from "lodash/range";
import messages from "./messages";
import * as constants from "./constants";

const {
  DATE_DISPLAY_FORMAT,
  DATE_FORMAT,
  TIME_DISPLAY_FORMAT,
  STANDARD_DATE_FORMAT,
} = constants;

let _intl = {
  formatMessage: () => {},
  formatNumer: () => {},
};

const initializeIntl = (intl) => {
  _intl = intl;
};

const isPreviousDate = (current) =>
  current < moment().subtract(1, "days").endOf("day");

const getPreviousHours = (date) => {
  const momentDate = date ? moment(date) : moment();
  return range(momentDate.hour());
};

const getPreviousMinutes = (date) => {
  const momentDate = date ? moment(date) : moment();
  return range(momentDate.minute());
};

const disableDateRange = (startDate, endDate) => (current) =>
  moment(endDate).endOf("day") < current ||
  current < moment(startDate).startOf("day");

const getRelativeDay = (date) => {
  const selectedFormat = "Do MMM";
  const todayDate = moment().format(selectedFormat);
  const yesterdayDate = moment().subtract(1, "days").format(selectedFormat);
  const tomorrowDate = moment().add(1, "days").format(selectedFormat);

  return moment(date).calendar(null, {
    sameDay: `[${_intl.formatMessage(messages.today)}, ${todayDate}]`,
    nextDay: `[${_intl.formatMessage(messages.tomorrow)}, ${tomorrowDate}]`,
    lastDay: `[${_intl.formatMessage(messages.yesterday)}, ${yesterdayDate}]`,
    sameElse: DATE_DISPLAY_FORMAT,
    nextWeek: DATE_DISPLAY_FORMAT,
    lastWeek: DATE_DISPLAY_FORMAT,
  });
};

const getEpochTimeStamp = ({ timezone, date, withMilliSeconds = true }) => {
  let method = "valueOf"; // Unix time in milliseconds: http://momentjs.com/docs/#/displaying/unix-timestamp-milliseconds/
  if (!withMilliSeconds) {
    method = "unix"; // unix time in seconds: http://momentjs.com/docs/#/displaying/unix-timestamp/
  }
  if (timezone) {
    return moment(date).tz(timezone)[method](); //eslint-disable-line prettier/prettier
  } else {
    return moment(date)[method]();
  }
};

/**
 * Gives ISO day of the week with 1 being Monday and 7 being Sunday in numbers for org settings
 * Org Monday = 0, Tuesday = 1, Wed = 2, Thur =3, Fri = 4, Sat = 5, Sun = 6
 * @param weekDay
 * @returns {number}
 */
function getISOWeekDayNumberForOrgSetting(weekDay) {
  if (isNaN(Number(weekDay)) || Number(weekDay) > 6 || Number(weekDay) < 0) {
    return 1;
  }
  if (Number(weekDay) === 6) return 0; //to handle Sunday of this week and not next sunday
  return Number(weekDay) + 1;
}

/**
 * base date range for last period
 * @param lastSyncDate
 * @param baseFilter
 * @param periodValue
 * @param weekStart
 * @returns {{start, end}}
 */
function getDateRangeForPeriod(
  lastSyncDate,
  baseFilter,
  periodValue,
  weekStart = 0
) {
  const dateRange = {};
  const now = moment(lastSyncDate);
  if (baseFilter === "week") {
    const weekStartDay = getISOWeekDayNumberForOrgSetting(weekStart);
    const startOfWeek = now.clone().isoWeekday(weekStartDay);
    if (startOfWeek.isAfter(now)) {
      startOfWeek.subtract(1, "week");
    }
    dateRange.start = startOfWeek.clone().subtract(periodValue, baseFilter);
    dateRange.end = startOfWeek.clone();
  } else if (baseFilter === "month") {
    dateRange.start = now.clone().subtract(periodValue, baseFilter);
    dateRange.end = now.clone();
  } else {
    dateRange.start = moment()
      .subtract(periodValue, baseFilter)
      .startOf(baseFilter);
    dateRange.end = now.clone();
  }
  return getFormattedDateRange(dateRange);
}

/**
 * Return formatted values for start and end of date range
 * @param dateRange
 * @param dateFormat
 * @returns {{start, end}}
 */
function getFormattedDateRange(dateRange, dateFormat = STANDARD_DATE_FORMAT) {
  return {
    start: dateRange.start.clone().format(dateFormat),
    end: dateRange.end.clone().format(dateFormat),
  };
}

const getDayDifference = (currentDate, compareDate) => {
  // startDate, endDate can't be epoch time in seconds, as the parsing is different for that
  const formattedCurrentDate = moment(currentDate).format(DATE_FORMAT);
  const formattedCompareDate = moment(compareDate).format(DATE_FORMAT);
  return moment(formattedCompareDate, DATE_FORMAT).diff(
    moment(formattedCurrentDate, DATE_FORMAT),
    "day"
  );
};

const msToDateFormat = (date) => moment(date).format(DATE_DISPLAY_FORMAT);

const msToTimeFormat = (date) => moment(date).format(TIME_DISPLAY_FORMAT);

/* unix() function gives number of seconds since epoch time,
  but for the api need to app number of days since epoch time.
  so deviding number of seconds since epoch with number of seconds a day to get number of days since epoch
  */

const getEpochDays = (date) =>
  date && Math.floor(moment(date.clone()).unix() / 86400); // 86400 === number of seconds a day

export default {
  initializeIntl,
  isPreviousDate,
  getPreviousHours,
  getPreviousMinutes,
  disableDateRange,
  getRelativeDay,
  getEpochTimeStamp,
  getISOWeekDayNumberForOrgSetting,
  getDateRangeForPeriod,
  getFormattedDateRange,
  getDayDifference,
  msToDateFormat,
  msToTimeFormat,
  getEpochDays,
};
